import { useCallback, useEffect, useRef } from 'react';
import { useIssueStore } from 'store/issue';
import { useLocation } from 'react-router-dom';
import { FormattedMessage } from 'intl';
import { formatTranslation } from 'intl';
import { useIntl } from 'react-intl';
import {
  CommonTranslationKeys,
  InspectionTranslationKeys,
  IssueTranslationKeys,
} from 'intl/keys';
import { translationKeyToString } from 'intl/types/translationKey';

import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { useBacktrack } from 'components/common/withBacktrack';
import { useRedirectContext } from 'components/common/withRedirect';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { useAppNavigation } from 'contexts/navigation';
import { mergeLocationState } from 'helpers/navigationState';
import { useProjectId } from 'store/project';
import { InspectionOnView } from 'presentation/inspection/inspectionOnView';
import { IssueModel } from 'shared/domain/issue/issueModel';
import { putPendingProtocolAnswersForChecklistItem } from 'shared/domain/inspection/mapping/putPendingProtocolAnswersForChecklistItem';
import { showErrorToastAndLog } from 'services/toaster';
import { logError } from 'setup/logger/logError';
import { isAbortError } from 'shared/utils/errorHandling';
import { APP_PATHS } from 'setup/navigation/paths';
import {
  useInspection,
  useInspectionStore,
  useInspectionSubmit,
  useInspectionEditPermission,
} from 'store/inspection';
import useFormEdit from 'views/issue/forms/useFormEdit';
import { AfterDoneResult } from 'views/issue/wizard/afterDoneResult';

import {
  CreateIssueDialogCloseHandler,
  ProtocolIdentification,
} from '../types';
import { resolveIssueNavigationId } from '../utils/issueIdentity';

type ProtocolItem = NonNullable<InspectionOnView['protocol']>[number];

function hasInspectionChanged(
  currentId: string | undefined,
  storedId: string | undefined
): boolean {
  return Boolean(currentId && storedId && currentId !== storedId);
}

export function useProtocolItemIssues(args: {
  checklistItemId: string;
  allExpanded?: boolean;
  inspection: InspectionOnView | undefined;
  protocolItem: ProtocolItem | undefined;
  setOpen: (open: boolean) => void;
}): {
  issueCardOnClick: (issueModel: IssueModel) => void;
  handleClose: CreateIssueDialogCloseHandler;
  createInspectionIssue: () => void;
  beforeSave: () => Promise<ProtocolIdentification>;
} {
  const { checklistItemId, allExpanded, inspection, protocolItem, setOpen } =
    args;

  const intl = useIntl();
  const projectId = useProjectId();
  const location = useLocation();
  const createDialog = useDialog();
  const { pushLocation } = useBacktrack();
  const { setCanRedirect, setRedirection } = useRedirectContext();
  const { goToIssueDetails, goToInspectionEdit } = useAppNavigation();
  const { values, submitForm, validate } = useInputForm();
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const { inspectionModel, getInspection } = useInspection();
  const inspectionTemplate = useInspectionStore.use.inspectionTemplate();
  const appendProtocolItemIssue =
    useInspectionStore.use.appendProtocolItemIssue();
  const appendIssueOnInspection = useIssueStore.use.addInspectionIssue();
  const putProtocolAnswer = useInspectionStore.use.putProtocolAnswer();
  const atomicSaveEnabled = useInspectionStore.use.atomicSaveEnabled();
  const editPermission = useInspectionEditPermission();
  const { setEditing, clearAllForms } = useFormEdit();
  const { setSkipRedirectAfterSave } = useInspectionSubmit();

  const protocolRef = useRef<ProtocolIdentification>({
    inspection: inspection?._id,
    protocolItem: protocolItem?._id,
  });
  const pendingAnswersAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      pendingAnswersAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const currentInspectionId = inspection?._id;
    const storedInspectionId = protocolRef.current.inspection;

    if (hasInspectionChanged(currentInspectionId, storedInspectionId)) {
      protocolRef.current = {
        inspection: currentInspectionId,
        protocolItem: protocolItem?._id,
      };
      return;
    }

    protocolRef.current = {
      inspection: currentInspectionId ?? storedInspectionId,
      protocolItem: protocolItem?._id ?? protocolRef.current.protocolItem,
    };
  }, [inspection, protocolItem]);

  const getInspectionId = useCallback(() => {
    return protocolRef.current.inspection ?? inspection?._id;
  }, [inspection?._id]);

  const getProtocolItemId = useCallback(() => {
    return protocolRef.current.protocolItem ?? protocolItem?._id;
  }, [protocolItem?._id]);

  const resetRedirectState = useCallback(() => {
    setCanRedirect(false);
    setRedirection(undefined);
    clearAllForms();
  }, [clearAllForms, setCanRedirect, setRedirection]);

  const pushInspectionReturnLocation = useCallback(
    (options: {
      inspectionId: string;
      protocolItemId?: string;
      preferEditRoute: boolean;
      enableEditing?: boolean;
    }) => {
      const { inspectionId, protocolItemId, preferEditRoute, enableEditing } =
        options;
      const templateId = inspection?.template?._id;
      const state = mergeLocationState(location.state, {
        templateId,
        checklistItemId,
        ...(allExpanded ? { allExpanded: true } : {}),
      });

      if (preferEditRoute) {
        const editState = enableEditing ? { ...state, enableEditing: true } : state;
        pushLocation({
          pathname: APP_PATHS.inspection.edit(projectId, inspectionId),
          state: editState,
        });
        if (enableEditing) {
          goToInspectionEdit(projectId, inspectionId, { state: editState });
        }
        return;
      }

      if (protocolItemId) {
        pushLocation({
          pathname: APP_PATHS.inspection.protocolItem(
            projectId,
            inspectionId,
            protocolItemId
          ),
          state,
        });
      }
    },
    [
      allExpanded,
      checklistItemId,
      goToInspectionEdit,
      inspection?.template?._id,
      location.state,
      projectId,
      pushLocation,
    ]
  );

  const navigateToIssue = useCallback(
    (issue: IssueModel, onAfterNavigate?: () => void) => {
      const issueNavigationId = resolveIssueNavigationId(issue);
      if (!issueNavigationId) {
        setSkipRedirectAfterSave(false);
        return;
      }

      goToIssueDetails(projectId, issueNavigationId, {
        state: { issue },
      });

      onAfterNavigate?.();
    },
    [goToIssueDetails, projectId, setSkipRedirectAfterSave]
  );

  const performIssueNavigation = useCallback(
    (
      issue: IssueModel,
      options: { preferEditRoute: boolean; enableEditing?: boolean }
    ) => {
      setSkipRedirectAfterSave(true);
      resetRedirectState();

      const inspectionId = getInspectionId();
      if (inspectionId) {
        pushInspectionReturnLocation({
          inspectionId,
          protocolItemId: getProtocolItemId(),
          preferEditRoute: options.preferEditRoute,
          enableEditing: options.enableEditing,
        });
      }

      // Defer issue navigation until pushInspectionReturnLocation is on the
      // backstack (manual pushLocation must precede the router PUSH).
      requestAnimationFrame(() => {
        navigateToIssue(issue, () => {
          if (atomicSaveEnabled) {
            return;
          }

          Promise.resolve()
            .then(() => submitForm())
            .catch((error) => {
              logError('Error saving inspection after redirect:', error);
              setSkipRedirectAfterSave(false);
            });
        });
      });
    },
    [
      atomicSaveEnabled,
      getInspectionId,
      getProtocolItemId,
      navigateToIssue,
      pushInspectionReturnLocation,
      resetRedirectState,
      setSkipRedirectAfterSave,
      submitForm,
    ]
  );

  const issueCardOnClick = useCallback(
    (issueModel: IssueModel) => {
      const isCreateRoute =
        location.pathname.includes('/create') ||
        location.pathname.includes('/inspectionTemplate/');
      const isEditingInspection = location.pathname.includes('/edit');

      if (atomicSaveEnabled) {
        performIssueNavigation(issueModel, {
          preferEditRoute: isEditingInspection,
        });
        return;
      }

      createDialog({
        title: <FormattedMessage id={IssueTranslationKeys.goToIssue} />,
        description: (
          <span>
            <FormattedMessage
              id={InspectionTranslationKeys.saveInspectionAutomatically}
            />
          </span>
        ),
        customControllerLabels: [
          translationKeyToString(CommonTranslationKeys.cancel),
          translationKeyToString(CommonTranslationKeys.yes),
        ],
        catchOnCancel: true,
      })
        .then(() => {
          performIssueNavigation(issueModel, {
            preferEditRoute: isEditingInspection,
          });

          if (!isCreateRoute) {
            return;
          }

          Promise.resolve()
            .then(() => submitForm())
            .then((response) => {
              const finalInspectionId =
                response?.inspection?._id ??
                protocolRef.current.inspection ??
                inspection?._id;

              if (response?.inspection?._id) {
                protocolRef.current.inspection = response.inspection._id;
                if (response.inspection.protocol) {
                  const savedProtocolItem = response.inspection.protocol.find(
                    (protocol: { templateChecklistItem: string; _id: string }) =>
                      protocol.templateChecklistItem === checklistItemId
                  );
                  if (savedProtocolItem?._id) {
                    protocolRef.current.protocolItem = savedProtocolItem._id;
                  }
                }
              }

              if (finalInspectionId) {
                pushInspectionReturnLocation({
                  inspectionId: finalInspectionId,
                  preferEditRoute: true,
                });
              }
            })
            .catch((error) => {
              logError('Error saving inspection after redirect:', error);
              setSkipRedirectAfterSave(false);
            });
        })
        .catch(() => {});
    },
    [
      atomicSaveEnabled,
      checklistItemId,
      createDialog,
      inspection?._id,
      location.pathname,
      performIssueNavigation,
      pushInspectionReturnLocation,
      setSkipRedirectAfterSave,
      submitForm,
    ]
  );

  const beforeSave = useCallback(async (): Promise<ProtocolIdentification> => {
    if (!protocolRef.current.inspection) {
      setSkipRedirectAfterSave(true);
      const submitResponse = await submitForm();
      protocolRef.current = {
        inspection: submitResponse.inspection._id,
        protocolItem: submitResponse.inspection.protocol.find(
          (item: { templateChecklistItem: string; _id: string }) =>
            item.templateChecklistItem === checklistItemId
        )?._id,
      };
    }

    return {
      inspection: protocolRef.current.inspection,
      protocolItem: protocolRef.current.protocolItem,
    };
  }, [checklistItemId, setSkipRedirectAfterSave, submitForm]);

  const createInspectionIssue = useCallback(() => {
    if (!editPermission.permission) {
      return;
    }

    if (validate()) {
      return;
    }

    if (protocolItem) {
      setOpen(true);
      return;
    }

    createDialog({
      title: formatTranslation(
        intl,
        InspectionTranslationKeys.itemCreateIssueWarningTitle
      ),
      description: formatTranslation(
        intl,
        InspectionTranslationKeys.itemCreateIssueWarning
      ),
      customControllerLabels: [
        translationKeyToString(CommonTranslationKeys.cancel),
        translationKeyToString(CommonTranslationKeys.bottomNavCreate),
      ],
      catchOnCancel: true,
    })
      .then(() => {
        setOpen(true);
      })
      .catch(() => {});
  }, [createDialog, editPermission.permission, intl, protocolItem, setOpen, validate]);

  const handleClose: CreateIssueDialogCloseHandler = useCallback(
    (maybeIssue, reason) => {
      if (reason === 'backdropClick') {
        return;
      }

      setOpen(false);
      setEditing(true, 'issue');

      if (reason !== AfterDoneResult.saved) {
        return;
      }

      if (!maybeIssue) {
        setSkipRedirectAfterSave(false);
        logError('Missing issue after creation; cannot navigate to issue.', {
          checklistItemId,
        });
        return;
      }

      const shouldReturnToEdit =
        location.state?.returnToEdit || location.state?.enableEditing;
      const protocolItemId =
        protocolRef.current.protocolItem ?? protocolItem?._id;

      if (protocolItemId) {
        appendProtocolItemIssue(protocolItemId, maybeIssue._id);
        appendIssueOnInspection(maybeIssue);
      }

      const inspectionId = protocolRef.current.inspection ?? inspection?._id;

      void (async () => {
        if (!atomicSaveEnabled && inspectionId && inspectionTemplate) {
          pendingAnswersAbortRef.current?.abort();
          const pendingAnswersController = new AbortController();
          pendingAnswersAbortRef.current = pendingAnswersController;

          try {
            await putPendingProtocolAnswersForChecklistItem({
              form: valuesRef.current,
              template: inspectionTemplate,
              protocolItems: inspectionModel?.protocol,
              checklistItemId,
              inspectionId,
              projectId,
              putProtocolAnswer,
              signal: pendingAnswersController.signal,
            });
          } catch (error) {
            if (
              pendingAnswersController.signal.aborted ||
              isAbortError(error)
            ) {
              return;
            }

            showErrorToastAndLog('Error while updating protocol item',
              error
            );
            getInspection();
            setSkipRedirectAfterSave(false);
            return;
          } finally {
            if (pendingAnswersAbortRef.current === pendingAnswersController) {
              pendingAnswersAbortRef.current = null;
            }
          }
        }

        getInspection();

        if (!atomicSaveEnabled) {
          try {
            await createDialog({
              title: <FormattedMessage id={IssueTranslationKeys.goToIssue} />,
              description: (
                <span>
                  <FormattedMessage
                    id={InspectionTranslationKeys.saveInspectionAutomatically}
                  />
                </span>
              ),
              customControllerLabels: [
                translationKeyToString(CommonTranslationKeys.cancel),
                translationKeyToString(CommonTranslationKeys.yes),
              ],
              catchOnCancel: true,
            });
          } catch {
            setSkipRedirectAfterSave(false);
            return;
          }
        }

        setSkipRedirectAfterSave(true);
        resetRedirectState();

        if (inspectionId) {
          pushInspectionReturnLocation({
            inspectionId,
            protocolItemId:
              protocolRef.current.protocolItem ?? protocolItem?._id,
            preferEditRoute: Boolean(shouldReturnToEdit),
            enableEditing: Boolean(shouldReturnToEdit),
          });
        }

        try {
          if (!atomicSaveEnabled) {
            await submitForm();
          }
          // Defer issue navigation until pushInspectionReturnLocation is on the
          // backstack (manual pushLocation must precede the router PUSH).
          requestAnimationFrame(() => {
            try {
              goToIssueDetails(projectId, maybeIssue._id);
            } catch (error) {
              logError('Error saving inspection before redirect:', error);
              setSkipRedirectAfterSave(false);
            }
          });
        } catch (error) {
          logError('Error saving inspection before redirect:', error);
          setSkipRedirectAfterSave(false);
        }
      })();
    },
    [
      appendIssueOnInspection,
      appendProtocolItemIssue,
      atomicSaveEnabled,
      checklistItemId,
      createDialog,
      getInspection,
      goToIssueDetails,
      inspection?._id,
      inspectionModel?.protocol,
      inspectionTemplate,
      location.state,
      projectId,
      protocolItem,
      pushInspectionReturnLocation,
      putProtocolAnswer,
      resetRedirectState,
      setEditing,
      setOpen,
      setSkipRedirectAfterSave,
      submitForm,
    ]
  );

  return {
    issueCardOnClick,
    handleClose,
    createInspectionIssue,
    beforeSave,
  };
}
